import { api } from '../../../core/network/api';
import { renderAsync } from '../async-view';
import { t } from '../../i18n';
import { playerCardPlate } from '../player-card-plate';
import { NO_CARD } from '../../../../../shared/src/player-cards.js';
import { badge, badgeArt, button, card, icon, type IconName, progressBar, sectionHeading, tabStrip, type BadgeArt } from '../ui';
import { countUp } from '../fx';
import type { RouteMount } from './types';

/**
 * The Leaderboards route.
 *
 * The four categories registered at startup by `arena-stats.service.ts` — read from that file,
 * not guessed. `GET /api/leaderboard/:category` is `@Public()`; `GET /api/leaderboard/:category/me`
 * requires auth, which every route in the shell already has.
 */
interface LeaderboardCategoryDef {
  key: string;
  labelKey: string;
  /** Drawn on the tab and in every value chip of that board. */
  art: IconName;
}

const CATEGORIES: LeaderboardCategoryDef[] = [
  { key: 'arena_kills', labelKey: 'leaderboards.kills', art: 'kills' },
  { key: 'arena_wins', labelKey: 'leaderboards.wins', art: 'trophy' },
  { key: 'arena_headshots', labelKey: 'leaderboards.headshots', art: 'headshot' },
  { key: 'rank_points', labelKey: 'leaderboards.rank', art: 'rank' },
];

interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  value: number;
}

interface PersonalRank {
  rank: number;
  value: number;
  total: number;
}

interface LeaderboardData {
  entries: LeaderboardEntry[];
  personal: PersonalRank;
  /**
   * Each ranked player's equipped card and title, keyed by user id.
   *
   * A SECOND request rather than extra fields on the ladder rows, because `getTopN` lives in
   * `backend/src/core/leaderboard/` and core is off-limits in a game repo — a game-specific
   * join belongs in `backend/src/game/`, which is exactly what `/game/profiles/cosmetics` is.
   *
   * Empty on failure, never fatal: a leaderboard whose cosmetics call 500s is a leaderboard
   * with plain names on it, which is what it was until 2026-09-17 anyway. The ladder is the
   * screen's job; the decoration is not worth an error state.
   */
  cosmetics: Map<string, { card: number; title: string | null }>;
}

/** One read of the OS setting per call — a person can change it mid-session, and `fx.ts`'s own
 * copy of this check is not exported for a route file to reuse. */
function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Career totals are denormalised onto every `User` row, so `getTopN` never actually returns
 * `[]` once more than zero accounts exist — a fresh account is ranked at value 0 the instant
 * it's created, before it has fired a shot. Listing every registered player as a "top kills"
 * entry at 0 is noise, not a leaderboard, and only gets worse as accounts accumulate (the
 * first real player buried under a wall of zeroes). Filtered here, client-side, rather than
 * asking the server to change what `getTopN` returns — `rank` on the rows that remain is left
 * exactly as the server sent it, never renumbered, so a displayed rank still matches the
 * player's actual rank.
 */
function withScore(entries: LeaderboardEntry[]): LeaderboardEntry[] {
  return entries.filter((entry) => entry.value > 0);
}

/**
 * The podium, top three.
 *
 * Rank 1 sits centre and tallest, rank 2 left, rank 3 right — the classic three-step stand —
 * done with `order-*` utilities rather than reordering the DOM, so the podium's own
 * `textContent` still reads rank 1, rank 2, rank 3 in that order (what a screen reader hears,
 * and what a test asserting "first three usernames in order" checks). Medal art comes straight
 * from the kit's `badgeArt` tiers: `gold`/`silver`/`bronze` already exist and already mean
 * exactly this on the Career screen, so this is the same visual language, not a new one. The
 * steps are painted plates (`kit-plate`) of three heights.
 */
const PODIUM_ART: readonly BadgeArt[] = ['gold', 'silver', 'bronze'];
const PODIUM_ORDER = ['order-2', 'order-1', 'order-3'];
const PODIUM_STAND_HEIGHT = [
  'h-[clamp(4.5rem,16vw,6.5rem)]',
  'h-[clamp(3.25rem,11vw,4.75rem)]',
  'h-[clamp(2.5rem,8vw,3.5rem)]',
];
const PODIUM_MEDAL_SIZE = ['w-[clamp(2.75rem,16vw,4rem)] h-[clamp(2.75rem,16vw,4rem)]', 'w-[clamp(2rem,12vw,3rem)] h-[clamp(2rem,12vw,3rem)]'];

/**
 * The equipped card and title for a page of ranked players.
 *
 * Failure is an empty map, deliberately — see `LeaderboardData.cosmetics`. An id the server did
 * not answer for (a deleted account still sitting in a cached ladder) simply has no entry, and
 * the row draws plain.
 */
async function loadCosmetics(userIds: readonly string[]): Promise<Map<string, { card: number; title: string | null }>> {
  const out = new Map<string, { card: number; title: string | null }>();
  if (userIds.length === 0) return out;
  try {
    const res = await api.get<{ profiles: Array<{ userId: string; equippedCard: number; titleName: string | null }> }>(
      `/game/profiles/cosmetics?userIds=${userIds.map(encodeURIComponent).join(',')}`,
    );
    for (const profile of res.profiles) out.set(profile.userId, { card: profile.equippedCard, title: profile.titleName });
  } catch {
    // Left empty — a decorated ladder and a plain one differ by decoration only.
  }
  return out;
}

/** What a row knows about its player's cosmetics. Absent and "owns none" are the same thing
 * here, and both draw plain — never a fallback frame for something nobody bought. */
type RowCosmetics = { card: number; title: string | null } | undefined;

/** The title line under a name on this screen, or null when there is none to draw. An empty
 * element would still cost the row its line height. */
function titleLine(cosmetics: RowCosmetics): HTMLElement | null {
  if (!cosmetics?.title) return null;
  const el = document.createElement('span');
  el.className = 'block font-heading uppercase tracking-[0.08em] text-[10px] text-gold/90 truncate';
  el.dataset.board = 'title';
  el.textContent = cosmetics.title;
  return el;
}

/** A figure in a painted slot, the board's own icon over its left end. */
function valueChip(art: IconName, value: string, size = 30): { el: HTMLElement; figure: HTMLElement } {
  const el = document.createElement('span');
  el.className = 'kit-chip shrink-0';
  el.style.setProperty('--kh', `${size}px`);
  const figure = document.createElement('span');
  figure.className = 'kit-label font-heading tabular-nums text-[14px]';
  figure.textContent = value;
  el.append(icon(art, { size: 24, className: '-ml-2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }), figure);
  return { el, figure };
}

function podiumColumn(
  entry: LeaderboardEntry,
  place: 1 | 2 | 3,
  isSelf: boolean,
  cosmetics: RowCosmetics,
  art: IconName,
): HTMLElement {
  const index = place - 1;
  const col = document.createElement('div');
  col.setAttribute('role', 'listitem');
  col.className = `kit-pop flex flex-col items-center min-w-0 flex-1 ${PODIUM_ORDER[index]}`;
  // The leader lands last, on top of the two steps beside it.
  col.style.animationDelay = `${(3 - place) * 90}ms`;

  const medal = badgeArt(PODIUM_ART[index]!, { size: place === 1 ? 64 : 48 });
  medal.className += ` mx-auto ${PODIUM_MEDAL_SIZE[place === 1 ? 0 : 1]}`;
  const medalSlot = document.createElement('div');
  medalSlot.className = 'relative';
  medalSlot.append(medal);
  if (place === 1) {
    // The one thing on the stand that idles: the trophy over the leader's head.
    medalSlot.append(icon('trophy', { size: 32, className: 'kit-float absolute -top-5 left-1/2 -translate-x-1/2 w-7 h-7 [filter:drop-shadow(0_3px_3px_rgba(0,0,0,0.7))]' }));
    medalSlot.classList.add('mt-5');
  }

  const nameRow = document.createElement('div');
  nameRow.className = 'flex items-center gap-1.5 mt-1.5 max-w-full';
  const name = document.createElement('span');
  name.className = 'kit-label font-heading uppercase text-[12px] truncate';
  name.textContent = entry.username;
  // `playerCardPlate` is the one renderer for this — the same element Career and the shell
  // header draw, so a card is the same card wherever a name appears. `NO_CARD` comes back as a
  // plain wrapper, which is why nothing here branches on "is a card worn".
  nameRow.append(playerCardPlate(cosmetics?.card ?? NO_CARD, name, { className: 'min-w-0 truncate' }));
  if (isSelf) nameRow.append(badge(t('leaderboards.you'), 'gold'));

  const chip = valueChip(art, '0', place === 1 ? 34 : 30);
  chip.el.classList.add('mt-1');

  const stand = document.createElement('div');
  stand.className = `kit-plate [--kp-rim:12px] [--kp-pad:0px] w-full mt-2 flex items-center justify-center ${PODIUM_STAND_HEIGHT[index]}` +
    (isSelf ? ' kit-selected' : '');
  const standRank = document.createElement('span');
  standRank.className = `kit-title font-heading font-extrabold ${place === 1 ? 'text-2xl' : 'text-lg'}`;
  standRank.textContent = `#${entry.rank}`;
  stand.append(standRank);

  const title = titleLine(cosmetics);
  col.append(medalSlot, nameRow, ...(title ? [title] : []), chip.el, stand);

  if (prefersReducedMotion()) {
    chip.figure.textContent = String(entry.value);
  } else {
    requestAnimationFrame(() => countUp(chip.figure, 0, entry.value, { format: (v) => String(Math.round(v)) }));
  }

  return col;
}

/** Renders as few as one column when the board has not filled up yet — the shape still reads
 * as "a podium with open steps", not a layout bug. */
function podium(
  top3: LeaderboardEntry[],
  selfId: string,
  cosmetics: Map<string, { card: number; title: string | null }>,
  art: IconName,
): HTMLElement | null {
  if (top3.length === 0) return null;
  const wrap = document.createElement('div');
  wrap.setAttribute('role', 'list');
  wrap.setAttribute('aria-label', t('leaderboards.podiumLabel'));
  wrap.className = 'flex items-end justify-center gap-2 sm:gap-4 mb-5 px-1';
  top3.forEach((entry, i) =>
    wrap.append(podiumColumn(entry, (i + 1) as 1 | 2 | 3, entry.userId === selfId, cosmetics.get(entry.userId), art)),
  );
  return wrap;
}

/** The initial of a name on a small painted plate — there is no avatar upload, so this is the face. */
function initialPlate(username: string): HTMLElement {
  const plate = document.createElement('span');
  plate.className = 'kit-plate [--kp-rim:9px] [--kp-pad:0px] shrink-0 grid place-items-center w-10 h-10';
  const letter = document.createElement('span');
  letter.className = 'kit-title font-heading uppercase text-[16px]';
  letter.textContent = username.slice(0, 1) || '?';
  plate.append(letter);
  return plate;
}

/**
 * One ladder row: a recess in the board with the rank in game lettering, the player's initial
 * plate, name (and title), and the figure in a slot. The viewer's own row glows (`kit-selected`).
 */
function entryRow(entry: LeaderboardEntry, isSelf: boolean, cosmetics: RowCosmetics, art: IconName): HTMLElement {
  const row = document.createElement('div');
  row.className = [
    'kit-well kit-deal flex items-center justify-between gap-2 sm:gap-3 min-h-[56px] py-2 px-2 sm:px-3',
    isSelf ? 'kit-selected' : '',
  ].join(' ');

  const left = document.createElement('div');
  left.className = 'flex items-center gap-2 sm:gap-3 min-w-0';
  const rank = document.createElement('span');
  rank.className = 'w-9 sm:w-11 shrink-0 text-center kit-label font-heading text-[15px] tabular-nums';
  rank.textContent = `#${entry.rank}`;
  const name = document.createElement('span');
  name.className = 'kit-label font-heading uppercase text-[14px] truncate';
  name.textContent = entry.username;
  // Name and title stack inside the plate, so the card frames both — a title floating outside
  // its own card would read as belonging to the row, not to the player.
  const identity = document.createElement('span');
  identity.className = 'block min-w-0';
  identity.append(name);
  const title = titleLine(cosmetics);
  if (title) identity.append(title);
  left.append(rank, initialPlate(entry.username), playerCardPlate(cosmetics?.card ?? NO_CARD, identity, { className: 'min-w-0 truncate' }));
  if (isSelf) left.append(badge(t('leaderboards.you'), 'gold'));

  row.append(left, valueChip(art, String(entry.value)).el);
  return row;
}

/** A numbered place nobody holds yet — quiet, because nothing here is interactive. */
function openSlot(position: number): HTMLElement {
  const slot = document.createElement('div');
  slot.className = 'kit-well kit-deal flex items-center gap-2 sm:gap-3 min-h-[48px] px-2 sm:px-3 opacity-70';
  const rank = document.createElement('span');
  rank.className = 'w-9 sm:w-11 shrink-0 text-center kit-label font-heading text-[14px] tabular-nums';
  rank.textContent = `#${position}`;
  const open = document.createElement('span');
  open.className = 'text-[12px] font-heading uppercase tracking-[0.14em] text-text-dim';
  open.textContent = t('leaderboards.openSlot');
  slot.append(rank, open);
  return slot;
}

/**
 * The viewer's own standing, pinned under the ladder as a glowing plate. The "#N of total" line
 * counts up on arrival; the bar under it shows how far the row directly above is — hidden when
 * nobody is above (rank 1) or the viewer is not on the visible ladder.
 */
function pinnedSelf(
  username: string,
  personal: PersonalRank,
  ranked: LeaderboardEntry[],
  selfId: string,
  art: IconName,
  cosmetics: RowCosmetics,
): HTMLElement {
  const plate = document.createElement('div');
  plate.dataset.row = 'self';
  plate.className = 'kit-plate kit-selected kit-pop flex flex-col gap-2 [--kp-rim:14px] [--kp-pad:12px_14px]';

  const line = document.createElement('div');
  line.className = 'flex items-center justify-between gap-2 sm:gap-3';
  const name = document.createElement('span');
  name.className = 'kit-label font-heading uppercase text-[14px] truncate';
  name.textContent = username;
  const place = document.createElement('span');
  place.className = 'block text-[11px] font-heading uppercase tracking-[0.1em] text-text-dim tabular-nums';
  const placeText = (rank: number) => t('leaderboards.rankOf', { rank, total: personal.total });
  place.textContent = placeText(personal.rank);
  const identity = document.createElement('span');
  identity.className = 'block min-w-0';
  identity.append(name, place);
  const title = titleLine(cosmetics);
  if (title) identity.append(title);
  const who = document.createElement('div');
  who.className = 'flex items-center gap-2 sm:gap-3 min-w-0';
  who.append(initialPlate(username), playerCardPlate(cosmetics?.card ?? NO_CARD, identity, { className: 'min-w-0 truncate' }), badge(t('leaderboards.you'), 'gold'));
  const chip = valueChip(art, '0', 34);
  line.append(who, chip.el);
  plate.append(line);

  countUp(place, 0, personal.rank, { format: (v) => placeText(Math.round(v)) });
  countUp(chip.figure, 0, personal.value, { format: (v) => String(Math.round(v)) });

  const selfIndex = ranked.findIndex((entry) => entry.userId === selfId);
  if (selfIndex > 0) {
    const above = ranked[selfIndex - 1]!;
    const gap = Math.max(0, above.value - personal.value);
    const gapText = t('leaderboards.gapToNext', { amount: gap, rank: above.rank });
    const gapLabel = document.createElement('p');
    gapLabel.className = 'text-[11px] text-text-dim font-heading uppercase tracking-[0.1em]';
    gapLabel.textContent = gapText;
    plate.append(gapLabel, progressBar({ value: personal.value, max: above.value, accent: 'cyan', label: gapText }));
  }
  return plate;
}

export const mountLeaderboardsRoute: RouteMount = (outlet, ctx) => {
  const heading = sectionHeading(t('leaderboards.title'));

  const content = document.createElement('div');

  let activeCategory = CATEGORIES[0]!.key;
  let disposeAsync: (() => void) | null = null;

  const strip = tabStrip(
    CATEGORIES.map((c) => ({ id: c.key, label: t(c.labelKey), art: c.art })),
    activeCategory,
    (id) => {
      if (id === activeCategory) return;
      activeCategory = id;
      strip.setActive(id);
      mountBoard();
    },
  );

  function mountBoard(): void {
    disposeAsync?.();
    disposeAsync = renderAsync<LeaderboardData>(content, {
      load: async () => {
        const [entries, personal] = await Promise.all([
          api.get<LeaderboardEntry[]>(`/leaderboard/${activeCategory}`),
          api.get<PersonalRank>(`/leaderboard/${activeCategory}/me`),
        ]);
        // Sequential, not parallel: the ids to ask about only exist once the ladder has
        // answered. One request for the whole page rather than one per row — the endpoint takes
        // up to 50 ids and this screen shows ten.
        const cosmetics = await loadCosmetics(entries.map((entry) => entry.userId));
        return { entries, personal, cosmetics };
      },
      // No `isEmpty` branch — the third and last screen to give up its whole-screen empty
      // state, after Career and Inventory. Measured, this route painted **9.7% of a 1440x900
      // viewport** and did not scroll — roughly 1.1 million pixels spent on a dimmed podium
      // icon and one sentence, on the screen whose entire job is to show a player what a
      // position in this game looks like.
      //
      // An empty ladder still has a shape — your own unranked row, the category you are
      // looking at, and the fact that the top ten slots are open. That reads as "these are
      // the places to take"; a podium at 45% opacity reads as "there is nothing here".
      renderContent(data, container) {
        container.innerHTML = '';

        const definition = CATEGORIES.find((c) => c.key === activeCategory);
        const label = definition ? t(definition.labelKey) : activeCategory;
        const art = definition?.art ?? 'rank';
        const ranked = withScore(data.entries);

        if (ranked.length === 0) {
          const callout = document.createElement('div');
          callout.dataset.board = 'first-run';
          callout.className = 'kit-plate kit-pop flex flex-wrap items-center justify-between gap-3 mb-4';
          const copy = document.createElement('p');
          copy.className = 'flex items-center gap-3 text-[13px] text-text min-w-0';
          copy.append(icon('trophy', { size: 40, className: 'kit-float w-10 h-10' }), t('leaderboards.emptyBoard', { label: label.toLowerCase() }));
          callout.append(copy, button(t('leaderboards.goToPlay'), { variant: 'primary', onClick: () => ctx.navigate('/play') }));
          container.append(callout);
        }

        // The podium — top three, brightest thing on the screen, first thing a player sees.
        const podiumEl = podium(ranked.slice(0, 3), ctx.user.id, data.cosmetics, art);

        // THE LADDER GOES IN A WINDOW. Rows are recesses cut into it; the podium already shows
        // the top three, so the rows start where it stops. The open places are numbered, so an
        // empty board is a set of places to take rather than an apology.
        const OPEN_SLOTS = 10;
        const list = document.createElement('div');
        list.className = 'flex flex-col gap-1.5';
        let step = 0;
        const stagger = (el: HTMLElement) => el.style.setProperty('--i', String(step++));
        for (const entry of ranked.slice(3)) {
          const row = entryRow(entry, entry.userId === ctx.user.id, data.cosmetics.get(entry.userId), art);
          stagger(row);
          list.append(row);
        }
        for (let position = ranked.length + 1; position <= OPEN_SLOTS; position++) {
          if (position <= 3 && ranked.length > 0) continue;
          const slot = openSlot(position);
          stagger(slot);
          list.append(slot);
        }

        // Your own standing, pinned under the ladder: placing, name, figure, and how far the
        // row above is. Always present once you are ranked, so a rank-400 player still sees
        // themselves without scrolling a list that stops at ten.
        const pinned = data.personal.rank > 0 ? pinnedSelf(ctx.user.username ?? ctx.user.email, data.personal, ranked, ctx.user.id, art, data.cosmetics.get(ctx.user.id)) : null;

        const board = card([list, ...(pinned ? [pinned] : [])], {
          title: t('leaderboards.topN', { count: OPEN_SLOTS }),
          headerTrailing: (() => {
            const count = document.createElement('span');
            count.className = 'kit-chip [--kh:28px] font-heading text-[11px] tracking-[0.12em] uppercase';
            count.textContent =
              ranked.length === 0 ? t('leaderboards.noEntries') : t('leaderboards.rankedCount', { count: ranked.length });
            return count;
          })(),
          padded: false,
        });

        if (podiumEl) container.append(podiumEl);
        container.append(board);
      },
    });
  }

  outlet.append(heading, strip.el, content);
  mountBoard();

  return () => {
    disposeAsync?.();
    heading.remove();
    strip.el.remove();
    content.remove();
  };
};
